import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import styled from "styled-components";
import Icon from "@sproutsocial/seeds-react-icon";
import { focusRing } from "@sproutsocial/seeds-react-mixins";
import type { TypeModalActionProps } from "../ModalTypes";
import { RAIL_BUTTON_SIZE } from "../../shared/constants";

/**
 * Action button component for the modal's floating rail.
 *
 * These buttons appear in the vertical rail next to the modal
 * and provide quick access to common actions. Supports two action types:
 * - "close": Automatically closes the modal when clicked
 * - "button" (default): Custom action with your own onClick handler
 *
 * @example
 * // Close button (built-in functionality)
 * <ModalAction
 *   actionType="close"
 *   aria-label="Close"
 *   iconName="x-outline"
 * />
 *
 * @example
 * // Custom action button
 * <ModalAction
 *   aria-label="Expand to fullscreen"
 *   iconName="arrows-pointing-out"
 *   onClick={() => console.log('expand')}
 * />
 */

const RailButton = styled.button`
  width: ${RAIL_BUTTON_SIZE}px;
  height: ${(props) => props.theme.space[500]};
  display: inline-grid;
  place-items: center;
  border-radius: ${(props) => props.theme.radii.outer};
  border: none;
  background: ${(props) => props.theme.colors.button.overlay.background.base};
  color: ${(props) => props.theme.colors.button.overlay.text.base};
  cursor: pointer;
  outline: none;
  transition: all ${(props) => props.theme.duration.fast}
    ${(props) => props.theme.easing.ease_inout};

  &:hover {
    background: ${(props) =>
      props.theme.colors.button.overlay.background.hover};
  }

  &:hover,
  &:active {
    transform: none;
  }

  &:focus-visible {
    ${focusRing}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ModalAction: React.FC<TypeModalActionProps> = ({
  "aria-label": ariaLabel,
  iconName,
  disabled,
  actionType,
  onClick,
  ...rest
}) => {
  const button = (
    <RailButton
      data-slot="modal-action"
      data-qa-modal-action
      data-qa-modal-action-type={actionType || "button"}
      aria-label={ariaLabel}
      title={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {iconName && <Icon name={iconName} size="small" aria-label={ariaLabel} />}
    </RailButton>
  );

  if (actionType === "close") {
    return <Dialog.Close asChild>{button}</Dialog.Close>;
  }

  return button;
};

ModalAction.displayName = "ModalAction";
